import React, { useState, useEffect, useRef } from 'react';
import './DatabaseTab.css';

// One searchable list of every unit, showing every field programmed onto the tag: the NDEF payload
// is SKU / SN / BatchID / AC (see encode_ndef_tlv in the GENF_MANUAL tool), plus the chip's own UID.
// Batch was the one tag field the API already returned but the table never showed.
// Search matches serial, tag UID, SKU, order # or school name — note batch is DISPLAYED but is not
// part of the server's broad `q` filter (searchBagsForAdmin), so don't advertise it here until it is.
// Server-side + capped, so it stays fast even against the full (57k+) production table.
// `initialQuery` is set when you arrive here from the global search — it seeds the box with the
// serial you clicked so that unit is already on screen.
function DatabaseTab({ initialQuery = '' }) {
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState([]);
  const [limited, setLimited] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  const api = process.env.REACT_APP_API_ENDPOINT;
  const authHeaders = () => ({
    'Authorization': 'Basic ' + btoa(`${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`),
  });

  const search = async (term) => {
    setLoading(true);
    try {
      const url = `${api}/admin/bags?limit=200${term ? `&q=${encodeURIComponent(term)}` : ''}`;
      const res = await fetch(url, { headers: authHeaders(), cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
        setLimited(!!data.limited);
      }
    } catch (e) { console.error('bag search failed', e); }
    finally { setLoading(false); }
  };

  // Re-runs when the global search sends a new serial in while this tab is already mounted.
  useEffect(() => {
    setQ(initialQuery);
    search(initialQuery.trim());
  }, [initialQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (v) => {
    setQ(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(v.trim()), 300);
  };

  const fmt = (ms) => {
    if (!ms) return '-';
    const d = new Date(ms);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const statusClass = (s) => ({
    'In warehouse': 'wh', 'At school': 'school', 'Assigned': 'assigned',
    'Lost': 'lost', 'Damaged': 'damaged', 'Archived': 'archived',
  })[s] || 'wh';

  return (
    <div className="database">
      <div className="database-header">
        <div>
          <h2>Database</h2>
          <p className="database-sub">Every unit, with every field on its tag. Search by serial, tag UID, SKU, order #, or school.</p>
        </div>
        <input
          className="database-search"
          value={q}
          onChange={e => onChange(e.target.value)}
          placeholder="Search serial, tag, SKU, order #, school…"
          autoFocus
        />
      </div>

      {limited ? <div className="database-limited">Showing the first {rows.length} matches. Refine your search to narrow it down.</div> : null}

      {loading && rows.length === 0 ? (
        <p className="database-empty">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="database-empty">No units match “{q}”.</p>
      ) : (
        <div className="database-table-wrap">
          <table className="database-table">
            <thead>
              <tr>
                <th>Serial</th><th>Tag UID</th><th>SKU</th><th>Batch</th><th>Order #</th>
                <th>School</th><th>Status</th><th>Student(s)</th><th>Scanned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.serialNumber + '-' + i}>
                  <td className="mono strong">{r.serialNumber || '-'}{r.customName ? <div className="database-custom">{r.customName}</div> : null}</td>
                  <td className="mono muted">{r.tagUid || '-'}</td>
                  <td>{r.model || '-'}</td>
                  <td className="mono">{r.batchId || '-'}</td>
                  <td className="mono">{r.ac || '-'}</td>
                  <td>{r.schoolName || '-'}</td>
                  <td><span className={`database-status ${statusClass(r.status)}`}>{r.status}</span></td>
                  <td>{r.students || '-'}</td>
                  <td className="nowrap">{fmt(r.scanTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DatabaseTab;
